(() => {
  const desktopMedia = window.matchMedia('(min-width: 768px)');

  const normalizeHeading = (value) => value.trim().replace(/\s+/g, ' ').toUpperCase();

  const hideRedundantDescriptionHeading = (root) => {
    const description = root.querySelector('.oldtrend-product-info__description');
    if (!description) return;

    const candidates = [...description.querySelectorAll('p, strong, b')].slice(0, 4);
    const heading = candidates.find(
      (element) => element.textContent.trim().toUpperCase() === 'PRODUCT DESCRIPTION'
    );

    if (heading) {
      const parent = heading.closest('p');
      (parent || heading).hidden = true;
    }
  };

  const moveDescriptionDetails = (root) => {
    const description = root.querySelector('.oldtrend-product-info__description');
    const destination = root.querySelector('[data-oldtrend-description-details]');
    if (!description || !destination || destination.dataset.oldtrendContentMoved === 'true') return;

    const detailHeadings = [...description.querySelectorAll('p, h2, h3, h4, h5, h6')];
    const firstDetailsHeading = detailHeadings.find((element) => {
      const heading = normalizeHeading(element.textContent);
      return heading === 'PRODUCT FEATURES' || heading === 'PRODUCT DIMENSIONS';
    });

    if (!firstDetailsHeading?.parentElement) return;

    const fragment = document.createDocumentFragment();
    let currentNode = firstDetailsHeading;

    while (currentNode) {
      const nextNode = currentNode.nextSibling;
      fragment.appendChild(currentNode);
      currentNode = nextNode;
    }

    destination.appendChild(fragment);

    if (root.querySelector('[data-oldtrend-product-sku]')) {
      const duplicateStyleNumber = [...destination.querySelectorAll('p, h2, h3, h4, h5, h6')].find(
        (element) => normalizeHeading(element.textContent).startsWith('STYLE #')
      );
      duplicateStyleNumber?.remove();
    }

    destination.dataset.oldtrendContentMoved = 'true';
  };

  const initializeProductInfo = (root) => {
    if (root.dataset.oldtrendInitialized === 'true') return;
    root.dataset.oldtrendInitialized = 'true';

    const tabs = [...root.querySelectorAll('[data-oldtrend-info-tab]')];
    const items = [...root.querySelectorAll('[data-oldtrend-info-item]')];
    const panels = [...root.querySelectorAll('[data-oldtrend-info-panel]')];
    const accordionButtons = [...root.querySelectorAll('[data-oldtrend-info-accordion]')];
    let activeKey = tabs[0]?.dataset.oldtrendInfoTab || 'description';

    const setDesktopPanel = (key, moveFocus = false) => {
      activeKey = key;

      tabs.forEach((tab) => {
        const selected = tab.dataset.oldtrendInfoTab === key;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && moveFocus) tab.focus();
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.oldtrendInfoPanel !== key;
      });
    };

    const setMobilePanel = (key, open) => {
      items.forEach((item) => {
        const isTarget = item.dataset.oldtrendInfoItem === key;
        item.classList.toggle('is-open', isTarget && open);

        const button = item.querySelector('[data-oldtrend-info-accordion]');
        const panel = item.querySelector('[data-oldtrend-info-panel]');
        if (button) button.setAttribute('aria-expanded', String(isTarget && open));
        if (panel) panel.hidden = !(isTarget && open);
      });
    };

    const syncLayout = () => {
      if (desktopMedia.matches) {
        setDesktopPanel(activeKey);
      } else {
        const openItem = items.find((item) => item.classList.contains('is-open'));
        const mobileKey = openItem?.dataset.oldtrendInfoItem || 'description';
        setMobilePanel(mobileKey, true);
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        setDesktopPanel(tab.dataset.oldtrendInfoTab);
      });

      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let targetIndex = index;
        if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;

        setDesktopPanel(tabs[targetIndex].dataset.oldtrendInfoTab, true);
      });
    });

    accordionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.oldtrendInfoAccordion;
        const item = button.closest('[data-oldtrend-info-item]');
        const shouldOpen = !item.classList.contains('is-open');
        activeKey = key;
        setMobilePanel(key, shouldOpen);
      });
    });

    const productSection = root.closest('[data-section-type="product"]');
    productSection?.addEventListener('theme:variant:change', (event) => {
      const sku = event.detail?.variant?.sku;
      const skuElement = root.querySelector('[data-oldtrend-product-sku]');
      if (skuElement && sku) skuElement.textContent = sku;
    });

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener('change', syncLayout);
    } else {
      desktopMedia.addListener(syncLayout);
    }

    hideRedundantDescriptionHeading(root);
    moveDescriptionDetails(root);
    syncLayout();
  };

  const initializeGallery = (root) => {
    if (root.dataset.oldtrendInitialized === 'true') return;
    root.dataset.oldtrendInitialized = 'true';

    const slideshow = root.querySelector('[data-product-slideshow]');
    const previous = root.querySelector('[data-oldtrend-gallery-previous]');
    const next = root.querySelector('[data-oldtrend-gallery-next]');
    if (!slideshow || !previous || !next) return;

    const mediaWrapper = root.closest('.product__media__wrapper');
    const thumbnailTrack = mediaWrapper?.querySelector('[data-product-thumbs]');
    const thumbnailPrevious = mediaWrapper?.querySelector('[data-thumbs-arrow-prev]');
    const thumbnailNext = mediaWrapper?.querySelector('[data-thumbs-arrow-next]');

    const changeSlide = (direction) => {
      const visibleSlides = [...slideshow.querySelectorAll('[data-media-slide]')].filter(
        (slide) => !slide.classList.contains('hide')
      );
      if (visibleSlides.length < 2) return;

      const currentIndex = visibleSlides.findIndex((slide) => slide.classList.contains('is-selected'));
      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const targetIndex = (safeIndex + direction + visibleSlides.length) % visibleSlides.length;
      const targetId = visibleSlides[targetIndex].dataset.mediaId;
      if (!targetId) return;

      slideshow.dispatchEvent(
        new CustomEvent('theme:image:change', {
          detail: { id: targetId },
        })
      );
    };

    previous.addEventListener('click', () => changeSlide(-1));
    next.addEventListener('click', () => changeSlide(1));

    slideshow.addEventListener('click', (event) => {
      if (event.target.closest('button, a, input, select, textarea, video, model-viewer')) return;

      const selectedImage = event.target.closest(
        '.product__media[data-type="image"].is-selected .product-image-wrapper'
      );
      const zoomButton = selectedImage?.querySelector('[data-zoom-button]');
      zoomButton?.click();
    });

    if (thumbnailTrack && thumbnailPrevious && thumbnailNext) {
      thumbnailPrevious.classList.add('oldtrend-thumbnail-arrow');
      thumbnailNext.classList.add('oldtrend-thumbnail-arrow');

      const updateThumbnailControls = () => {
        const maxScroll = Math.max(0, thumbnailTrack.scrollHeight - thumbnailTrack.clientHeight);
        thumbnailPrevious.disabled = thumbnailTrack.scrollTop <= 2;
        thumbnailNext.disabled = thumbnailTrack.scrollTop >= maxScroll - 2;
      };

      const scrollThumbnails = (direction) => {
        const thumbnail = thumbnailTrack.querySelector('[data-slideshow-thumbnail]');
        const distance = thumbnail ? thumbnail.getBoundingClientRect().height + 16 : 146;
        thumbnailTrack.scrollBy({ top: distance * direction, behavior: 'smooth' });
      };

      thumbnailPrevious.addEventListener(
        'click',
        (event) => {
          event.stopImmediatePropagation();
          scrollThumbnails(-1);
        },
        true
      );
      thumbnailNext.addEventListener(
        'click',
        (event) => {
          event.stopImmediatePropagation();
          scrollThumbnails(1);
        },
        true
      );
      thumbnailTrack.addEventListener('scroll', updateThumbnailControls, { passive: true });
      window.addEventListener('resize', updateThumbnailControls);
      updateThumbnailControls();
    }
  };

  const initializeCompleteLook = (root) => {
    if (root.dataset.oldtrendInitialized === 'true') return;
    root.dataset.oldtrendInitialized = 'true';

    const track = root.querySelector('[data-complete-look-track]');
    const previous = root.querySelector('[data-complete-look-prev]');
    const next = root.querySelector('[data-complete-look-next]');
    if (!track || !previous || !next) return;

    const updateControls = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    const scrollTrack = (direction) => {
      const item = track.querySelector('[data-complete-look-item]');
      const distance = item ? item.getBoundingClientRect().width + 12 : track.clientWidth;
      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    previous.addEventListener('click', () => scrollTrack(-1));
    next.addEventListener('click', () => scrollTrack(1));
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);
    updateControls();
  };

  const initialize = (container = document) => {
    container.querySelectorAll('[data-oldtrend-product-gallery]').forEach(initializeGallery);
    container.querySelectorAll('[data-oldtrend-product-info]').forEach(initializeProductInfo);
    container.querySelectorAll('[data-oldtrend-complete-look]').forEach(initializeCompleteLook);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
